import {useMemo} from 'react'
import {useLocation} from '@github-ui/react-router'
import {clsx} from 'clsx'
import {Flash, Link} from '@primer/react'
import {type QueryError, type ErrorRange, PARSE_ERROR_FATAL} from '../types/blackbird-types'
import {AlertIcon} from '@primer/octicons-react'
import styles from './SearchError.module.css'

export default function SearchError({error, index}: {error: QueryError; index: number}) {
  const {search} = useLocation()
  const urlSearchParams = new URLSearchParams(search)
  const query = urlSearchParams.get('q')

  const variant = !error.type || error.type === PARSE_ERROR_FATAL ? 'danger' : 'warning'

  return (
    <Flash className={styles.flash} key={index} variant={variant} id="search-error-message-flash">
      <AlertIcon />
      {error.message}
      <QueryHelpComponent query={query} ranges={error.ranges} />
      <SuggestionComponent suggestion={error.suggestion} />
    </Flash>
  )
}

function QueryHelpComponent({query, ranges}: {query: string | null; ranges?: ErrorRange[]}) {
  const queryHelpComponent = useMemo(() => {
    const queryHelpSpans: React.ReactElement[] = []
    if (ranges && query) {
      let offset = 0
      for (const range of ranges) {
        const start = range.start ?? 0
        const end = range.end ?? 0

        // Emit any prefix
        const inner = query.slice(offset, start)
        if (inner.length > 0) queryHelpSpans.push(<span key={`help-inner-${inner}`}>{inner}</span>)
        offset = start

        // Emit the highlighted range
        const warn = query.slice(range.start, end)
        if (warn.length > 0)
          queryHelpSpans.push(
            <span key={`help-warning-${warn}`} className={styles.warningText}>
              {warn}
            </span>,
          )
        offset = end
      }

      // Emit any final suffix
      const suffix = query.slice(offset)
      if (suffix.length > 0) queryHelpSpans.push(<span key={`help-${suffix}`}>{suffix}</span>)

      if (queryHelpSpans.length > 0) {
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        return <div className={clsx('code-font-mono', styles.queryHelp)}>{queryHelpSpans}</div>
      }
    }
    return null
  }, [ranges, query])

  return queryHelpComponent
}

function SuggestionComponent({suggestion}: {suggestion?: string}) {
  const suggestionComponent = useMemo(() => {
    if (suggestion) {
      const url = `?q=${encodeURIComponent(suggestion)}`

      // TODO: Copied from blackbird-fe - we don't currently support repo/scopes
      // if (repoName) {
      //   url = `/${repoName}${url}`;
      // }

      // if (scopeName) {
      //   url += `&scopeName=${encodeURIComponent(
      //     scopeName
      //   )}&scope=${encodeURIComponent(scope)}`;
      // }

      return (
        <div className={styles.suggestionWrapper}>
          <span className={styles.suggestionText}>
            Suggestion:{' '}
            <Link
              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
              className={clsx('code-font-mono', styles.monoLink)}
              href={url}
            >
              {suggestion}
            </Link>
          </span>
        </div>
      )
    }
    return null
  }, [suggestion])

  return suggestionComponent
}
